import React, {
  useMemo,
  useRef,
  useCallback,
  useState,
  useEffect,
} from "react";
import {
  EyeIcon,
  ArrowUpOnSquareIcon,
  ArrowDownOnSquareIcon,
  ClipboardDocumentIcon,
  TrashIcon,
} from "@heroicons/react/24/solid";
import { Plus as PlusIcon } from "lucide-react";
import { useModelStore } from "@/store/useModelStore";
import { usePropertyStore } from "@/store/usePropertyStore";
import {
  exportSectionPropertiesToCSV,
  importSectionPropertiesFromCSV,
  copySectionPropertiesToClipboard,
  processExcelFile,
  createFileValidationError,
  generateFilename,
} from "@/utilities/CSVUtils";
import { BeamIDsManualInputDialog } from "../dialogs/BeamIDsManualInputDialog";
import { EditPropertyDialog } from "../dialogs/EditPropertyDialog";
import {
  TableLayout,
  TableColumn,
} from "../../workspace/components/ui/TableLayout";
import { useWorkspaceState } from "../../../../src/components/workspace/hooks/core/useWorkspaceState";
import { useCursorManager } from "../../menu/hooks/useCursorManager";
import {
  useTableSelection,
  useCursorMode,
  useCancelAllApplyModes,
} from "@/hooks/useGlobalSelection";
import { useGlobalSelectionStore } from "@/store/useGlobalSelectionStore";
import {
  validatePropertyBeamReferences,
  validateDuplicateSectionAssignments,
} from "@/utils/structuralValidation";

/**
 * Property Definition Interface
 * Represents a unique property ID and all beams using it
 */
interface PropertyDefinition {
  P_ID: string;
  beamIds: string[];
  beamCount: number;
  material?: string;
  sectionType?: string;
  designation?: string;
}

interface SectionPropertiesTableProps {
  onOpenConfig?: () => void;
}

export const SectionPropertiesTable: React.FC<SectionPropertiesTableProps> = ({
  onOpenConfig,
}) => {
  const beams = useModelStore((state) => state.beams);
  const properties = usePropertyStore((state) => state.properties);
  const setEditingPropertyIdStore = usePropertyStore(
    (state) => state.setEditingPropertyId
  );
  const addProperty = usePropertyStore((state) => state.addProperty);
  const applyPropertyToBeams = usePropertyStore(
    (state) => state.applyPropertyToBeams
  );
  const deleteProperty = usePropertyStore((state) => state.deleteProperty);

  const { state, callbacks } = useWorkspaceState();
  const { setCursorByState } = useCursorManager();

  // Apply by Click mode from store
  const applyByClickMode = usePropertyStore((state) => state.applyByClickMode);
  const activePropertyId = usePropertyStore(
    (state) => state.activePropertyIdForClick
  );
  const setApplyByClickMode = usePropertyStore(
    (state) => state.setApplyByClickMode
  );
  const setActivePropertyId = usePropertyStore(
    (state) => state.setActivePropertyIdForClick
  );

  // Apply to Selected mode (local state)
  const [applyToSelectedMode, setApplyToSelectedMode] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);

  // Import process state
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [csvImportPropertyId, setCSVImportPropertyId] = useState<string | null>(
    null
  );

  // Edit property dialog (local, not store)
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingPropertyId, setEditingPropertyIdLocal] = useState<
    string | null
  >(null);

  // Activation banner
  const [activationMessage, setActivationMessage] = useState<string | null>(
    null
  );

  // Inline CSV editing state for Beam(s) column
  const [editingBeamIds, setEditingBeamIds] = useState<{
    propertyId: string;
    value: string;
  } | null>(null);

  const { selectRow, unselectRow, isRowSelected } =
    useTableSelection("section-properties");
  const { setCursorMode, clearCursorMode } = useCursorMode();
  const cancelAllModes = useCancelAllApplyModes();
  const globalActiveTable = useGlobalSelectionStore(
    (state) => state.activeTable
  );
  const globalActiveRowId = useGlobalSelectionStore(
    (state) => state.activeRowId
  );
  const prevActiveTable = useRef<string | null>(null);

  // Start editing beam IDs
  const startEditingBeams = useCallback(
    (e: React.MouseEvent, propertyId: string, currentBeamIds: string[]) => {
      e.stopPropagation();
      setEditingBeamIds({
        propertyId,
        value: currentBeamIds.join(", "),
      });
    },
    []
  );

  // Commit edited beam IDs
  const commitBeamIdsEdit = useCallback(() => {
    if (!editingBeamIds) return;

    const input = editingBeamIds.value.trim();

    // Parse CSV - split by comma, trim whitespace, filter empty
    const beamIdsArray = Array.from(
      new Set(
        input
          .split(",")
          .map((id) => id.trim())
          .filter((id) => id !== "")
      )
    );

    // If empty, allow it (unassign all beams from this property)
    if (beamIdsArray.length === 0) {
      applyPropertyToBeams(editingBeamIds.propertyId, []);
      setEditingBeamIds(null);
      return;
    }

    // Validation: Check all are numeric
    const nonNumeric = beamIdsArray.filter((id) => !/^\d+$/.test(id));
    if (nonNumeric.length > 0) {
      alert(
        `Invalid beam IDs (must be numeric only):\n${nonNumeric.join(", ")}`
      );
      setEditingBeamIds(null);
      return;
    }

    // Validation: Check if beams exist
    const validBeamIds = new Set(beams.map((b) => b.id));
    const missingBeams = beamIdsArray.filter((id) => !validBeamIds.has(id));

    if (missingBeams.length > 0) {
      alert(
        `The following beam IDs do not exist in the beam table:\n${missingBeams.join(", ")}`
      );
      setEditingBeamIds(null);
      return;
    }

    // Validation: Check if any beam already has a different property
    const conflictingBeams = getBeamsWithExistingProperties(
      beamIdsArray,
      editingBeamIds.propertyId,
      properties,
      beams
    );

    if (conflictingBeams.length > 0) {
      const conflicts = conflictingBeams
        .map((c) => `Beam ${c.beamId} (has P_ID: ${c.propertyId})`)
        .join("\n");
      alert(
        `Cannot assign property - the following beams already have different properties:\n\n${conflicts}`
      );
      setEditingBeamIds(null);
      return;
    }

    // Apply property to beams - this updates the property store and beam store
    applyPropertyToBeams(editingBeamIds.propertyId, beamIdsArray);
    setEditingBeamIds(null);
  }, [editingBeamIds, beams, properties, applyPropertyToBeams]);

  const getBeamsWithExistingProperties = useCallback(
    (
      beamIds: string[],
      currentPropertyId: string,
      allProperties: any[],
      beams: any[]
    ): { beamId: string; propertyId: string }[] => {
      // ✅ Use centralized validation
      const errors = validateDuplicateSectionAssignments(
        allProperties.map((p) => ({
          P_ID: p.P_ID,
          beamIds: p.beamIds || [],
        })),
        beams.map((b) => b.id)
      );

      // Convert validation errors to conflict format
      const conflicts: { beamId: string; propertyId: string }[] = [];

      beamIds.forEach((beamId) => {
        const beam = beams.find((b: any) => b.id === beamId);
        if (
          beam?.P_ID &&
          beam.P_ID !== "-" &&
          beam.P_ID !== "default" &&
          beam.P_ID !== currentPropertyId
        ) {
          conflicts.push({ beamId, propertyId: beam.P_ID });
        }
      });

      return conflicts;
    },
    []
  );

  // Auto-deselect when switching to another table
  useEffect(() => {
    if (
      prevActiveTable.current === "section-properties" &&
      globalActiveTable !== "section-properties" &&
      globalActiveTable !== null
    ) {
      unselectRow();
      setActivePropertyId(null);
      setApplyByClickMode(false);
      setApplyToSelectedMode(false);
      clearCursorMode();
      setActivationMessage(null);
    }

    prevActiveTable.current = globalActiveTable;
  }, [globalActiveTable]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        if (applyByClickMode) {
          event.preventDefault();
          cancelApplyByClickMode();
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [applyByClickMode]);

  useEffect(() => {
    const handleGlobalCancel = () => {
      unselectRow();
      setActivePropertyId(null);
      setApplyByClickMode(false);
      setApplyToSelectedMode(false);
      clearCursorMode();
      setActivationMessage(null);
    };

    window.addEventListener("cancelAllTableSelections", handleGlobalCancel);
    return () =>
      window.removeEventListener(
        "cancelAllTableSelections",
        handleGlobalCancel
      );
  }, [unselectRow, clearCursorMode]);

  useEffect(() => {
    const handleCancelPropertyModes = () => {
      unselectRow();
      setActivePropertyId(null);
      setApplyByClickMode(false);
      setApplyToSelectedMode(false);
      clearCursorMode();
      setActivationMessage(null);
    };

    window.addEventListener(
      "cancelPropertyApplyModes",
      handleCancelPropertyModes
    );
    return () =>
      window.removeEventListener(
        "cancelPropertyApplyModes",
        handleCancelPropertyModes
      );
  }, [
    unselectRow,
    clearCursorMode,
    setActivePropertyId,
    setApplyByClickMode,
    setApplyToSelectedMode,
  ]);

  // Catch store-level validation blocks
  useEffect(() => {
    const handlePropertyBlocked = (event: CustomEvent) => {
      const { conflicts, newPID } = event.detail;
      setErrorMessage(
        `Cannot assign P_ID "${newPID}" to beams with existing properties:\n\n` +
          `${conflicts}\n\n` +
          `Remove existing properties first.`
      );
      setTimeout(() => setErrorMessage(null), 5000);
    };

    window.addEventListener(
      "propertyAssignmentBlocked",
      handlePropertyBlocked as EventListener
    );
    return () =>
      window.removeEventListener(
        "propertyAssignmentBlocked",
        handlePropertyBlocked as EventListener
      );
  }, []);

  useEffect(() => {
    if (!applyByClickMode || !activePropertyId) return;

    console.log("useEffect ACTIVE - listening for beamClicked");

    const handleBeamClick = (event: CustomEvent) => {
      const beamId = event.detail?.beamId;
      console.log(
        "SectionPropertiesTable Beam clicked:",
        beamId,
        "Active PID:",
        activePropertyId
      );

      if (!beamId) return;

      const beam = beams.find((b: any) => b.id === beamId);
      if (beam?.P_ID === activePropertyId) {
        console.log("SAME PID already assigned to beam", beamId, "- Skipping!");
        setActivationMessage(
          `PID ${activePropertyId} already on Beam ${beamId}`
        );
        setTimeout(() => setActivationMessage(null), 2000);
        return;
      }

      // IMMEDIATE validation - use the SAME function as manual input
      const beamsWithProps = getBeamsWithExistingProperties(
        [beamId],
        activePropertyId,
        properties,
        beams
      );

      if (beamsWithProps.length > 0) {
        console.log("BLOCKED! Existing PID:", beamsWithProps[0].propertyId);
        setErrorMessage(
          `Beam ${beamId} already has property ${beamsWithProps[0].propertyId} assigned! Each beam can only have ONE section property. Remove the existing property first, then assign a new one.`
        );
        return;
      }

      console.log("NO CONFLICT - Applying...");

      const currentProperty = properties.find(
        (p) => p.P_ID === activePropertyId
      );

      // Get all currently assigned beams (both from 3D model and pending)
      const assignedBeams = beams.filter(
        (beam: any) => beam.P_ID === activePropertyId
      );
      const assignedBeamIds = assignedBeams.map((beam: any) => beam.id);
      const pendingBeamIds = currentProperty?.beamIds || [];
      const allCurrentIds = [
        ...new Set([...assignedBeamIds, ...pendingBeamIds]),
      ];

      // ✅ ADD the new beam to the existing list (don't replace)
      const updatedBeamIds = [...allCurrentIds, beamId];

      applyPropertyToBeams(activePropertyId, updatedBeamIds);

      setActivationMessage(`Added Beam ${beamId} to PID ${activePropertyId}`);
      setTimeout(() => setActivationMessage(null), 2000);
    };

    window.addEventListener("beamClicked", handleBeamClick as EventListener);
    return () => {
      console.log("useEffect cleanup - removing listener");
      window.removeEventListener(
        "beamClicked",
        handleBeamClick as EventListener
      );
    };
  }, [
    applyByClickMode,
    activePropertyId,
    beams,
    properties,
    getBeamsWithExistingProperties,
    applyPropertyToBeams,
  ]);

  /**
   * Use properties from store and display ALL beam IDs (existing + pending)
   */
  const propertyDefinitions = useMemo<PropertyDefinition[]>(() => {
    return properties
      .map((property) => {
        // Beams that already have this property applied (from 3D model)
        const assignedBeams = beams.filter(
          (beam) => beam.P_ID === property.P_ID
        );
        const assignedBeamIds = assignedBeams.map((beam) => beam.id);

        // Pending beam IDs from property store
        const pendingBeamIds = property.beamIds || [];

        // Combine both and remove duplicates
        const allBeamIds = [
          ...new Set([...assignedBeamIds, ...pendingBeamIds]),
        ];

        // Sort numerically
        const sortedBeamIds = allBeamIds.sort((a, b) => {
          const numA = parseInt(a);
          const numB = parseInt(b);
          return numA - numB;
        });

        return {
          P_ID: property.P_ID,
          beamIds: sortedBeamIds,
          beamCount: sortedBeamIds.length,
          material: property.material,
          sectionType: property.sectionType,
          designation: property.sectionProperties?.designation,
        };
      })
      .sort((a, b) => a.P_ID.localeCompare(b.P_ID));
  }, [properties, beams]);

  /**
   * Handle property edit action - Opens read-only property details dialog
   */
  const handleEditProperty = (pId: string) => {
    setEditingPropertyIdLocal(pId);
    setIsEditDialogOpen(true);
  };

  /**
   * Handle new property configuration
   */
  const handleConfigClick = () => {
    setEditingPropertyIdStore(null);
    if (onOpenConfig) onOpenConfig();
  };

  const handleExport = useCallback(() => {
    const filename = generateFilename("section-properties");
    exportSectionPropertiesToCSV(properties, { filename });
  }, [properties]);

  /**
   * Manual Input / CSV import for beam IDs
   */
  const handleCSVImportClick = () => {
    if (!activePropertyId) {
      setErrorMessage(
        "Please select a property row first, then click Manual Input."
      );
      return;
    }

    // Get the current property to find existing beam IDs
    const currentProperty = properties.find((p) => p.P_ID === activePropertyId);

    // Get beams that already have this property assigned (from 3D model)
    const assignedBeams = beams.filter(
      (beam) => beam.P_ID === activePropertyId
    );
    const assignedBeamIds = assignedBeams.map((beam) => beam.id);

    // Get pending beam IDs from property store
    const pendingBeamIds = currentProperty?.beamIds || [];

    // Combine both and remove duplicates
    const existingBeamIds = [
      ...new Set([...assignedBeamIds, ...pendingBeamIds]),
    ];

    console.log("📋 Pre-filling manual input with beam IDs:", existingBeamIds);

    setCSVImportPropertyId(activePropertyId);
  };

  const handleCSVImport = useCallback(
    (propertyId: string, beamIds: string[]) => {
      // Get the current property
      const currentProperty = properties.find((p) => p.P_ID === propertyId);
      if (!currentProperty) {
        setErrorMessage("Property not found.");
        return;
      }

      // Get currently assigned beam IDs
      const assignedBeams = beams.filter((beam) => beam.P_ID === propertyId);
      const currentlyAssignedIds = assignedBeams.map((beam) => beam.id);
      const currentPendingIds = currentProperty.beamIds || [];
      const allCurrentIds = [
        ...new Set([...currentlyAssignedIds, ...currentPendingIds]),
      ];

      // Find beams to ADD (new ones not currently assigned)
      const beamsToAdd = beamIds.filter((id) => !allCurrentIds.includes(id));

      // Find beams to REMOVE (currently assigned but not in new list)
      const beamsToRemove = allCurrentIds.filter((id) => !beamIds.includes(id));

      console.log("🔄 Manual Input - Changes:", {
        toAdd: beamsToAdd,
        toRemove: beamsToRemove,
        final: beamIds,
      });

      if (beamsToAdd.length > 0) {
        // 1. Check if beams exist
        const validationErrors = validatePropertyBeamReferences(
          [{ P_ID: propertyId, beamIds: beamsToAdd }],
          beams.map((b) => b.id)
        );

        if (validationErrors.length > 0) {
          const missingBeams = beamsToAdd.filter(
            (beamId) => !beams.find((b: any) => String(b.id) === String(beamId))
          );
          setErrorMessage(
            `Cannot assign property to ${missingBeams.length} beams (${missingBeams.join(", ")}).\n\n` +
              `These beams do not exist in the model.\n\n` +
              `Please create them first, then try again.`
          );
          return;
        }

        // 2. Check for duplicate section assignments (multiple properties on same beam)
        const allPropertiesAfterAdd = properties.map((p) =>
          p.P_ID === propertyId
            ? { ...p, beamIds: [...(p.beamIds || []), ...beamsToAdd] }
            : p
        );

        const duplicateErrors = validateDuplicateSectionAssignments(
          allPropertiesAfterAdd.map((p) => ({
            P_ID: p.P_ID,
            beamIds: p.beamIds || [],
          })),
          beams.map((b) => b.id)
        );

        if (duplicateErrors.length > 0) {
          setErrorMessage(
            `Cannot assign property - validation failed:\n\n` +
              duplicateErrors.map((e) => `• ${e.message}`).join("\n")
          );
          return;
        }
      }

      // CHECK if any NEW beam already has a different property
      if (beamsToAdd.length > 0) {
        const beamsWithProps = getBeamsWithExistingProperties(
          beamsToAdd,
          propertyId,
          properties,
          beams
        );

        if (beamsWithProps.length > 0) {
          const conflicts = beamsWithProps
            .map((b) => `Beam ${b.beamId} → ${b.propertyId}`)
            .join("\n");

          setErrorMessage(
            `Cannot assign property to ${beamsWithProps.length} beam(s):\n\n` +
              `${conflicts}\n\n` +
              `These beams already have section properties assigned.\n` +
              `Each beam can only have ONE section property.\n\n` +
              `Remove existing properties first, then try again.`
          );
          return;
        }
      }

      // Remove beams from this property
      if (beamsToRemove.length > 0) {
        const propertyStore = usePropertyStore.getState();
        propertyStore.removePropertyFromBeams(propertyId, beamsToRemove);
        console.log("🗑️ Removed beams:", beamsToRemove);
      }

      // Apply property to ALL beams in the final list
      applyPropertyToBeams(propertyId, beamIds);
      setCSVImportPropertyId(null);

      const addedCount = beamsToAdd.length;
      const removedCount = beamsToRemove.length;
      const message =
        addedCount > 0 && removedCount > 0
          ? `✅ Updated P_ID "${propertyId}": Added ${addedCount}, Removed ${removedCount} beam(s)`
          : addedCount > 0
            ? `✅ Added ${addedCount} beam(s) to P_ID "${propertyId}"`
            : removedCount > 0
              ? `✅ Removed ${removedCount} beam(s) from P_ID "${propertyId}"`
              : `✅ P_ID "${propertyId}" unchanged (${beamIds.length} beam(s))`;

      setActivationMessage(message);
      setTimeout(() => setActivationMessage(null), 3000);
    },
    [beams, properties, applyPropertyToBeams, getBeamsWithExistingProperties]
  );

  // Helper function to click toolbar buttons (same pattern as ModelingMenu)
  const clickToolbarButton = useCallback((searchTexts: string[]) => {
    const buttons = document.querySelectorAll("button");
    for (const text of searchTexts) {
      const button = Array.from(buttons).find((btn) => {
        const btnText = btn.textContent?.trim();
        const isModelingButton = btn.closest('[class*="bg-gray-8"]') !== null;
        if (!isModelingButton) return false;
        return btnText === text || btnText?.includes(text);
      });
      if (button && button.getAttribute("aria-pressed") === "true") {
        button.click();
        return true;
      }
    }
    return false;
  }, []);

  // helper function to disable all modeling modes
  const disableAllModes = useCallback(() => {
    const modelStore = useModelStore.getState();

    // Click toolbar buttons to toggle them off
    clickToolbarButton(["Create Node", "Node"]);
    clickToolbarButton(["Create Beam", "Beam"]);
    clickToolbarButton(["Prep Mode"]);
    clickToolbarButton(["Divide Beam"]);

    // Cancel states in callbacks
    if (callbacks?.setIsNodeCreationMode) {
      callbacks.setIsNodeCreationMode(false);
    }

    if (callbacks?.setIsBeamCreationMode) {
      callbacks.setIsBeamCreationMode(false);
    }

    // Cancel prep mode
    if (modelStore.isPrepMode) {
      modelStore.cancelPrepMode();
    }

    // Cancel divide beam mode
    if (modelStore.isDivideBeamMode) {
      modelStore.cancelDivideBeamMode();
    }

    // Cancel beam creation
    if (modelStore.cancelBeamCreation) {
      modelStore.cancelBeamCreation();
    }

    // Reset cursor
    clearCursorMode();
  }, [callbacks, clearCursorMode]);

  /**
   * "Apply by Click" mode
   */
  const handleApplyByClick = () => {
    if (properties.length === 0) {
      setErrorMessage(
        "No properties available. Please create a property first."
      );
      return;
    }

    setApplyByClickMode(true);

    setCursorMode("property-apply-click", activePropertyId);
  };

  const cancelApplyByClickMode = () => {
    setApplyByClickMode(false);
    clearCursorMode();
    unselectRow();
    setActivePropertyId(null);
    setActivationMessage("Apply by Click mode canceled");
    setTimeout(() => setActivationMessage(null), 2000);
  };

  /**
   * "Apply to Selected" mode
   */
  const handleApplyToSelected = useCallback(() => {
    const selectedBeams = beams.filter((b: any) => b.isSelected);
    if (selectedBeams.length === 0) {
      setErrorMessage("No beams selected.");
      return;
    }

    if (!activePropertyId) {
      setErrorMessage("Please select a property row first.");
      return;
    }

    const beamIds = selectedBeams.map((b: any) => b.id);

    // 1. Validate beam references exist
    const validationErrors = validatePropertyBeamReferences(
      [{ P_ID: activePropertyId, beamIds }],
      beams.map((b: any) => b.id)
    );

    if (validationErrors.length > 0) {
      const missingBeams = beamIds.filter(
        (beamId) => !beams.find((b: any) => String(b.id) === String(beamId))
      );
      setErrorMessage(
        `Cannot apply property - ${missingBeams.length} selected beams do not exist (${missingBeams.join(", ")})`
      );
      return;
    }

    // 2. Check for duplicate section assignments
    const beamsWithProps = getBeamsWithExistingProperties(
      beamIds,
      activePropertyId,
      properties,
      beams
    );

    if (beamsWithProps.length > 0) {
      const conflicts = beamsWithProps
        .map((b) => `Beam ${b.beamId} (${b.propertyId})`)
        .join(", ");
      setErrorMessage(
        `Cannot assign property to ${beamsWithProps.length} beams: ${conflicts}\n\n` +
          `These beams already have section properties assigned.\n\n` +
          `Each beam can only have ONE section property.`
      );
      return;
    }

    // Apply property
    applyPropertyToBeams(activePropertyId, beamIds);
    useModelStore.getState().clearSelection();

    setActivationMessage(
      `✅ Applied P_ID ${activePropertyId} to ${beamIds.length} beams`
    );
    setTimeout(() => setActivationMessage(null), 2000);
  }, [
    beams,
    activePropertyId,
    properties,
    applyPropertyToBeams,
    getBeamsWithExistingProperties,
  ]);

  const cancelApplyToSelectedMode = () => {
    setApplyToSelectedMode(false);
    setActivationMessage(null);
  };

  /**
   * Row click handler for both modes
   */
  const handlePropertyRowClickForSelected = (pId: string) => {
    if (!applyToSelectedMode) return;

    const selectedBeams = beams.filter((b) => b.isSelected);
    if (selectedBeams.length === 0) {
      setErrorMessage("No beams selected.");
      return;
    }

    const selectedBeamIds = selectedBeams.map((b) => b.id);
    applyPropertyToBeams(pId, selectedBeamIds);

    const modelStore = useModelStore.getState();
    modelStore.clearSelection();

    setApplyToSelectedMode(false);
    setActivationMessage(
      `Applied P_ID "${pId}" to ${selectedBeamIds.length} beam(s): ${selectedBeamIds.join(", ")}`
    );
    setTimeout(() => setActivationMessage(null), 3000);
  };

  const handlePropertyRowClick = useCallback(
    (pId: string, row: PropertyDefinition) => {
      cancelAllModes();

      disableAllModes();
      if (isRowSelected(pId)) {
        unselectRow();
        setActivePropertyId(null);
        setActivationMessage(`PID ${pId} deactivated.`);
      } else {
        selectRow(pId, row);
        setActivePropertyId(pId);
        setActivationMessage(`PID ${pId} activated! Click beams to assign.`);
      }
      setTimeout(() => setActivationMessage(null), 2000);
    },
    [
      cancelAllModes,
      disableAllModes,
      isRowSelected,
      unselectRow,
      selectRow,
      setActivePropertyId,
    ]
  );

  /**
   * File input handler (properties import)
   */
  const handleFileSelected = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;

      const fileExtension = file.name.split(".").pop()?.toLowerCase();

      setIsProcessing(true);
      setErrorMessage(null);

      try {
        let result: any;

        if (fileExtension === "csv") {
          result = await importSectionPropertiesFromCSV(file);
        } else if (fileExtension === "xlsx" || fileExtension === "xls") {
          const excelResult = await processExcelFile(file);
          if (excelResult.success) {
            const transformedData = excelResult.data.map((prop: any) => ({
              P_ID: prop.P_ID?.toString() || prop.PropertyID?.toString() || "",
              beamIds: (prop.BeamIDs || prop.BeamIds || prop.beamIds || "")
                .toString()
                .split(/[;,]/)
                .map((id: string) => id.trim())
                .filter(Boolean),
              material: prop.material || prop.Material || "None",
              sectionType: prop.sectionType || prop.SectionType,
              sectionProperties: prop.sectionProperties || {},
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
            }));

            result = {
              ...excelResult,
              data: transformedData,
              preview: transformedData.slice(0, 5),
            };
          } else {
            result = excelResult;
          }
        } else {
          setErrorMessage(
            createFileValidationError(file, ["csv", "xlsx", "xls"])
          );
          setIsProcessing(false);
          return;
        }

        if (result.success && result.data.length > 0) {
          // Calculate immediate vs. pending assignments
          const existingBeamIds = beams.map((b) => b.id);
          let immediateCount = 0;
          let pendingCount = 0;

          result.data.forEach((prop: any) => {
            (prop.beamIds || []).forEach((beamId: string) => {
              if (existingBeamIds.includes(beamId)) {
                immediateCount++;
              } else {
                pendingCount++;
              }
            });
          });

          // Show confirmation with counts
          const confirmMsg =
            `Import ${result.data.length} section properties?\n\n` +
            `✅ ${immediateCount} beam(s) will be assigned immediately\n` +
            `⏳ ${pendingCount} beam(s) will be assigned when created\n\n` +
            `⚠️ This will REPLACE all existing properties. Continue?`;

          if (confirm(confirmMsg)) {
            // Clear all existing properties first
            const propertyStore = usePropertyStore.getState();

            // Get all existing property IDs
            const existingPropertyIds = propertyStore.properties.map(
              (p) => p.P_ID
            );

            // Delete all existing properties (this also clears visualizations)
            existingPropertyIds.forEach((pId) => {
              propertyStore.deleteProperty(pId);
            });

            console.log(
              " Cleared all existing properties:",
              existingPropertyIds.length
            );

            // Reset all beams P_ID to default
            const modelStore = useModelStore.getState();
            const resetBeams = modelStore.beams.map((beam) => ({
              ...beam,
              P_ID: "default",
            }));
            modelStore.setBeams(resetBeams);
            console.log(" Reset all beam P_IDs to default");

            // Import all new properties
            result.data.forEach((prop: any) => {
              propertyStore.addProperty(prop);
            });

            console.log(" Imported new properties:", result.data.length);

            // Force re-render of 3D sections
            setTimeout(() => {
              propertyStore.autoApplyPendingProperties();
              console.log("Applied pending properties to existing beams");
            }, 100);

            // Show success message
            alert(
              `✅ Import completed!\n\n` +
                `${result.data.length} properties imported\n` +
                `${immediateCount} beams assigned immediately\n` +
                `${pendingCount} beams pending creation\n\n` +
                `All previous properties have been replaced.`
            );
          }
        } else {
          setErrorMessage(result.errors.join("\n"));
        }
      } catch (error) {
        console.error("File processing error:", error);
        setErrorMessage(
          "An error occurred while processing the file. Please try again."
        );
      } finally {
        setIsProcessing(false);
      }

      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    },
    [beams]
  );

  const handleCopy = useCallback(async () => {
    const success = await copySectionPropertiesToClipboard(properties);
    if (success) {
      alert("Section properties copied to clipboard!");
    } else {
      alert("Failed to copy to clipboard. Please try again.");
    }
  }, [properties]);

  /**
   * Handle delete for a property
   * Removes the property and unassigns it from all beams
   */
  const handleDelete = (row: PropertyDefinition) => {
    const beamCount = row.beamIds?.length || 0;
    const confirmMsg =
      beamCount > 0
        ? `Delete property "${row.P_ID}" and unassign it from ${beamCount} beam(s)?`
        : `Delete property "${row.P_ID}"?`;

    if (confirm(confirmMsg)) {
      deleteProperty(row.P_ID);

      unselectRow();
      setApplyByClickMode(false);
      setApplyToSelectedMode(false);
      setActivePropertyId(null);
      clearCursorMode();

      // Show confirmation message
      setActivationMessage(`✅ Deleted property "${row.P_ID}"`);
      setTimeout(() => setActivationMessage(null), 2000);
    }
  };

  // ------------------- TableLayout config (DATA-DRIVEN MODE) -------------------

  const theader: TableColumn[] = [
    {
      colname: "ID",
      field: "P_ID",
      className: "w-[100px]",
    },

    {
      colname: "Beam(s)",
      className: "flex-1",
      render: (row: PropertyDefinition) => {
        // If editing this property's beams
        if (editingBeamIds?.propertyId === row.P_ID) {
          return (
            <input
              type="text"
              autoFocus
              className="w-full h-8 px-2 border rounded text-black text-sm"
              value={editingBeamIds.value}
              onClick={(e) => e.stopPropagation()}
              onChange={(e) =>
                setEditingBeamIds((prev) =>
                  prev ? { ...prev, value: e.target.value } : prev
                )
              }
              onBlur={commitBeamIdsEdit}
              onKeyDown={(e) => {
                if (e.key === "Enter") commitBeamIdsEdit();
                if (e.key === "Escape") setEditingBeamIds(null);
              }}
              placeholder="e.g. 1, 2, 3"
            />
          );
        }

        // Display mode - click to edit (including empty state)
        const displayText =
          row.beamIds && row.beamIds.length > 0
            ? row.beamIds.join(", ")
            : "(none)";

        return (
          <span
            className="inline-flex items-center h-8 w-full px-2 bg-gray-50 rounded cursor-text text-black text-sm"
            onClick={(e) => startEditingBeams(e, row.P_ID, row.beamIds || [])}
          >
            {displayText}
          </span>
        );
      },
    },
    {
      colname: "Material",
      className: "w-[120px]",
      render: (row: PropertyDefinition) => row.material || "-",
    },
    {
      colname: "Section Type",
      className: "w-[150px]",
      render: (row: PropertyDefinition) => row.sectionType || "-",
    },
    {
      colname: "Designation",
      className: "w-[180px]",
      render: (row: PropertyDefinition) => row.designation || "-",
    },
    {
      colname: "Actions",
      className: "w-[100px] text-center",
      render: (row: PropertyDefinition) => (
        <div className="flex gap-1 justify-center">
          <button
            className="text-blue-500 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 transition-colors p-1"
            onClick={(e) => {
              e.stopPropagation();
              handleEditProperty(row.P_ID);
            }}
            title="View Details"
          >
            <EyeIcon className="h-4 w-4" />
          </button>
          <button
            className="text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 transition-colors p-1"
            onClick={(e) => {
              e.stopPropagation();
              handleDelete(row);
            }}
            title="Delete Property"
          >
            <TrashIcon className="h-4 w-4" />
          </button>
        </div>
      ),
    },
  ];

  const getRowClassName = (row: PropertyDefinition) => {
    const isSelected =
      globalActiveTable === "section-properties" &&
      globalActiveRowId === row.P_ID;

    if (isSelected) {
      return "selected-table-row bg-blue-50 dark:bg-gray-700";
    }
    if (applyToSelectedMode) {
      return "bg-purple-50";
    }
    return "";
  };

  return (
    <>
      <TableLayout
        title="Section Properties"
        theader={theader}
        tdata={propertyDefinitions}
        getRowClassName={getRowClassName}
        onRowClick={(row: PropertyDefinition) =>
          handlePropertyRowClick(row.P_ID, row)
        }
        actions={
          <>
            <button
              onClick={() => fileInputRef.current?.click()}
              className="p-1 hover:bg-gray-600 rounded"
              title="Import from CSV"
            >
              <ArrowDownOnSquareIcon className="h-5 w-5" />
            </button>

            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileSelected}
              accept=".csv,.xlsx,.xls"
              style={{ display: "none" }}
            />

            <button
              onClick={handleExport}
              className="p-1 hover:bg-gray-600 rounded"
              title="Export to CSV"
            >
              <ArrowUpOnSquareIcon className="h-5 w-5" />
            </button>

            <button
              onClick={handleCopy}
              className="p-1 hover:bg-gray-600 rounded"
              title="Copy to Clipboard"
            >
              <ClipboardDocumentIcon className="h-5 w-5" />
            </button>

            <button
              onClick={handleConfigClick}
              className="px-3 py-1.5 text-xs rounded bg-blue-500 text-white flex items-center gap-1 hover:bg-blue-600"
            >
              <PlusIcon className="h-4 w-4" />
              Add
            </button>
          </>
        }
        extra={
          globalActiveTable === "section-properties" && activePropertyId ? (
            <div>
              {activePropertyId &&
                !applyByClickMode &&
                !applyToSelectedMode && (
                  <div className="flex gap-2 justify-center">
                    <button
                      onClick={() => {
                        unselectRow();
                        clearCursorMode();
                        setActivePropertyId(null);
                        setActivationMessage("Selection canceled.");
                        setTimeout(() => {
                          setActivationMessage(null);
                        }, 2000);
                      }}
                      className="px-3 py-1.5 text-xs font-medium bg-red-500 text-white rounded hover:bg-red-600"
                    >
                      Cancel
                    </button>

                    <button
                      onClick={handleApplyByClick}
                      className="px-3 py-1.5 text-xs font-medium bg-blue-500 text-white rounded hover:bg-blue-600"
                    >
                      Apply by Click
                    </button>

                    <button
                      onClick={handleApplyToSelected}
                      className="px-3 py-1.5 text-xs font-medium bg-purple-500 text-white rounded hover:bg-purple-600"
                      disabled={beams.filter((b) => b.isSelected).length === 0}
                    >
                      Apply to Selected
                    </button>

                    <button
                      onClick={handleCSVImportClick}
                      className="px-3 py-1.5 text-xs font-medium bg-green-500 text-white rounded hover:bg-green-600"
                    >
                      Manual Input
                    </button>
                  </div>
                )}

              {applyByClickMode && (
                <div className="flex gap-2 justify-center">
                  <button
                    onClick={() => {
                      cancelApplyByClickMode();
                      setActivePropertyId(null);
                    }}
                    className="px-3 py-1.5 text-xs font-medium bg-red-500 text-white rounded hover:bg-red-600"
                  >
                    Cancel Apply by Click
                  </button>
                  <span className="px-3 py-1.5 text-xs font-medium text-blue-600 dark:text-blue-400">
                    Active: P_ID {activePropertyId}
                  </span>
                </div>
              )}

              {applyToSelectedMode && (
                <div className="flex gap-2 justify-center">
                  <button
                    onClick={() => {
                      cancelApplyToSelectedMode();
                      setActivePropertyId(null);
                    }}
                    className="px-3 py-1.5 text-xs font-medium bg-red-500 text-white rounded hover:bg-red-600"
                  >
                    Cancel Apply to Selected
                  </button>
                  <span className="px-3 py-1.5 text-xs font-medium text-purple-600 dark:text-purple-400">
                    {beams.filter((b) => b.isSelected).length} beam(s) selected
                  </span>
                </div>
              )}
            </div>
          ) : null
        }
      />

      {/* CSV Import Dialog (choose property) */}
      {/* {isCSVImportDialogOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-xl max-w-md w-full">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
              Select Property for CSV Import
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Choose a property to assign beam IDs from CSV file.
            </p>

            <select
              value={csvImportPropertyId || ""}
              onChange={(e) => setCSVImportPropertyId(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 mb-4"
            >
              <option value="">-- Select Property --</option>
              {properties.map((prop) => (
                <option key={prop.P_ID} value={prop.P_ID}>
                  {prop.P_ID}
                  {prop.sectionProperties?.designation
                    ? ` - ${prop.sectionProperties.designation}`
                    : ""}
                </option>
              ))}
            </select>

            <div className="flex justify-end gap-2">
              <button
                onClick={() => {
                  setIsCSVImportDialogOpen(false);
                  setCSVImportPropertyId(null);
                }}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-200 dark:bg-gray-600 rounded hover:bg-gray-300 dark:hover:bg-gray-500"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (!csvImportPropertyId) {
                    setErrorMessage("Please select a property.");
                    return;
                  }
                  setIsCSVImportDialogOpen(false);
                }}
                disabled={!csvImportPropertyId}
                className={`px-4 py-2 rounded ${
                  csvImportPropertyId
                    ? "bg-blue-600 text-white hover:bg-blue-700"
                    : "bg-gray-400 text-gray-200 cursor-not-allowed"
                }`}
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      )} */}

      {/* Beam IDs Manual Input Dialog */}
      {csvImportPropertyId && (
        <BeamIDsManualInputDialog
          isOpen={true}
          onClose={() => setCSVImportPropertyId(null)}
          onImport={(beamIds: string[]) =>
            handleCSVImport(csvImportPropertyId, beamIds)
          }
          propertyId={csvImportPropertyId}
          existingBeamIds={(() => {
            const currentProperty = properties.find(
              (p) => p.P_ID === csvImportPropertyId
            );
            const assignedBeams = beams.filter(
              (beam) => beam.P_ID === csvImportPropertyId
            );
            const assignedBeamIds = assignedBeams.map((beam) => beam.id);
            const pendingBeamIds = currentProperty?.beamIds || [];
            return [...new Set([...assignedBeamIds, ...pendingBeamIds])];
          })()}
        />
      )}

      {/* Activation Message */}
      {activationMessage && (
        <div className="fixed top-20 left-1/2 transform -translate-x-1/2 z-50 bg-blue-600 text-white px-6 py-3 rounded-lg shadow-xl">
          <p className="text-sm font-medium">{activationMessage}</p>
        </div>
      )}

      {/* Error Message Dialog */}
      {errorMessage && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-xl max-w-md">
            <h3 className="text-lg font-semibold text-red-600 dark:text-red-400 mb-2">
              Import Error
            </h3>
            <p className="text-gray-700 dark:text-gray-300 mb-4 whitespace-pre-line">
              {errorMessage}
            </p>
            <div className="flex justify-end">
              <button
                onClick={() => setErrorMessage(null)}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Processing Indicator */}
      {isProcessing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-xl">
            <p className="text-gray-700 dark:text-gray-300">
              Processing file...
            </p>
          </div>
        </div>
      )}

      {/* Edit Property Dialog */}
      {isEditDialogOpen && editingPropertyId && (
        <EditPropertyDialog
          isOpen={isEditDialogOpen}
          propertyId={editingPropertyId}
          onClose={() => {
            setIsEditDialogOpen(false);
            setEditingPropertyIdLocal(null);
          }}
        />
      )}
    </>
  );
};
